import { googleLogout } from '@react-oauth/google'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { axiosPrivate } from '../api/axios'
import useAuth from '../auth/hooks/useAuth'
import useRefreshToken from '../auth/hooks/useRefreshToken'
import { authActions } from '../auth/slices/auth.slice'
import useDeleteToken from './useDeleteToken'
import ROUTES from '../route/routes'

/**
 * Custom hook that provides an Axios instance with interceptors for handling
 * authentication and token refresh logic. It ensures that requests include
 * the appropriate authorization headers and handles token expiration or
 * invalidation scenarios.
 *
 * @returns {import('axios').AxiosInstance} An Axios instance with configured interceptors.
 *
 * @description
 * - The request interceptor adds the `Authorization` header with the user's token
 *   if it is not already present in the request.
 * - The response interceptor handles:
 *   - 401 Unauthorized errors by logging the user out, deleting the token, and
 *     redirecting to a session-expired page.
 *   - 403 Forbidden errors by attempting to refresh the token and retrying the
 *     original request with the new token.
 * - Both interceptors are cleaned up when the component using this hook unmounts.
 *
 * @requires useRefreshToken - A custom hook to refresh the user's access token.
 * @requires useAuth - A custom hook to access the authentication context.
 * @requires useDeleteToken - A custom hook to delete the user's token.
 * @requires useDispatch - A Redux hook to dispatch actions.
 * @requires useNavigate - A React Router hook to navigate between routes.
 */
const useAxiosPrivate = () => {
  const refresh = useRefreshToken()
  const dispatch = useDispatch()
  const { auth } = useAuth()
  const deleteToken = useDeleteToken()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${auth?.user?.token}`
        }
        return config
      },
      (error) => {
        console.error('Request error:', error)
        return Promise.reject(error)
      }
    )

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config

        if (error?.response?.status === 400) {
          // Clear session-related data
          try {
            googleLogout()
            const deleteRes = await deleteToken()
            if (deleteRes.status === 200) {
              dispatch(authActions.logout())
              navigate(ROUTES.PUBLIC.SESSION_EXPIRED, { state: { from: location }, replace: true })
            }
          } catch (logoutError) {
            console.error('Error during logout:', logoutError)
          }
          return Promise.reject(error)
        }

        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true
          try {
            const newAccessToken = await refresh()
            prevRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
            return axiosPrivate(prevRequest)
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError)
            return Promise.reject(refreshError)
          }
        }

        console.error('Response error:', error)
        return Promise.reject(error)
      }
    )

    return () => {
      axiosPrivate.interceptors.response.eject(responseIntercept)
      axiosPrivate.interceptors.request.eject(requestIntercept)
    }
  }, [auth, refresh, navigate, deleteToken, dispatch, location])

  return axiosPrivate
}

export default useAxiosPrivate
